/* eslint-disable react/jsx-props-no-spreading */
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { Button, Spinner } from '@micromag/core/components';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
// import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import useStoryPublicationCreate from '../../hooks/useStoryPublicationCreate';
import useStorySocket from '../../hooks/useStorySocket';
import * as AppPropTypes from '../../lib/PropTypes';

import { useOrganisation } from '../../contexts/OrganisationContext';
import PublicationServices from '../lists/PublicationServices';

const propTypes = {
    story: MicromagPropTypes.story.isRequired,
    services: AppPropTypes.publicationServices.isRequired,
    className: PropTypes.string,
    onPublished: PropTypes.func,
};

const defaultProps = {
    className: null,
    onPublished: null,
};

const StoryPublishForm = ({ story, services, className, onPublished }) => {
    const [publicationValue, setPublicationValue] = useState(null);
    const { create: createPublication, creating: apiCreating } = useStoryPublicationCreate(
        story.id,
    );

    const [socketPublications, setSocketPublications] = useState([]);

    const isCreating =
        apiCreating || socketPublications.filter(({ creating }) => creating).length > 0;
    const isPending = isCreating || socketPublications.filter(({ pending }) => pending).length > 0;

    const onSocketMessage = useCallback(
        ({ event = null, data }) => {
            if (
                event === 'publications.created' ||
                event === 'publications.published' ||
                event === 'publications.unpublished' ||
                event === 'publications.failed'
            ) {
                const { id: dataId = null } = data || {};
                setSocketPublications((old) => {
                    const existingIndex = old.findIndex(({ id }) => id === dataId);
                    if (existingIndex > -1) {
                        const newPending = [...old];
                        newPending[existingIndex] = { ...data, creating: false };
                        return newPending;
                    }
                    return [...old, { ...data, creating: false }];
                });
            }
        },
        [setSocketPublications],
    );
    useStorySocket(story, onSocketMessage);

    const organisation = useOrganisation();
    const hasOrganisation = organisation !== null;
    const availableServices = services.filter(({ id }) => hasOrganisation || id === 'micromag');

    const onClickPublish = useCallback(() => {
        Promise.all(
            Object.keys(publicationValue)
                .filter((id) =>
                    isObject(publicationValue[id])
                        ? publicationValue[id].enabled || false
                        : publicationValue[id] || false,
                )
                .map((id) => {
                    const value = publicationValue[id];
                    const settings = isObject(value) ? value.settings || null : null;
                    return createPublication(id, settings).then((res) => {
                        setSocketPublications((old) => {
                            const existingIndex = old.findIndex(
                                ({ id: dataId }) => dataId === res.id,
                            );
                            if (existingIndex > -1) {
                                const newPending = [...old];
                                newPending[existingIndex] = { ...res, creating: true };
                                return newPending;
                            }
                            return [...old, { ...res, creating: true }];
                        });
                        return res;
                    });
                }),
        );
    }, [createPublication, onPublished, publicationValue, setSocketPublications]);

    const hasServiceEnabled =
        publicationValue !== null &&
        Object.keys(publicationValue).reduce(
            (oneEnabled, serviceId) =>
                oneEnabled ||
                (isObject(publicationValue[serviceId])
                    ? publicationValue[serviceId].enabled || false
                    : publicationValue[serviceId] || false),
            false,
        );
    return (
        <form className={className}>
            <PublicationServices
                items={availableServices}
                story={story}
                value={publicationValue}
                onChange={setPublicationValue}
            />
            <div className="d-flex mt-4 align-items-center justify-content-end">
                {isCreating ? <Spinner className="me-2" /> : null}
                <Button
                    theme="primary"
                    size="lg"
                    disabled={!hasServiceEnabled || isPending}
                    onClick={onClickPublish}
                >
                    <FormattedMessage defaultMessage="Publish" description="Button label" />
                </Button>
            </div>
        </form>
    );
};

StoryPublishForm.propTypes = propTypes;
StoryPublishForm.defaultProps = defaultProps;

export default StoryPublishForm;
